import { ReactNode, useEffect, useRef, useState } from "react";
import dropdownStyles from "../../styles/UI/accordion.module.css";
import { ReactComponent as CheckIcon } from "../../assets/icons/check_white.svg";

type AccordionListProps<T> = {
  id?: string;
  options: T[];
  labelKey?: keyof T;
  selectedOption?: T;
  leadingElement?: (option: T, isSelected: boolean) => ReactNode;
  onSelect: (option: T) => void;
  onClickAway?: () => void;
  onEscape?: () => void;
  width?: string;
  right?: string;
  height?: string;
  label?: string;
  isDisabled?: (option: T) => boolean;
};

const AccordionList = <T,>({
  id,
  options,
  labelKey,
  selectedOption,
  leadingElement,
  onSelect,
  onClickAway,
  onEscape,
  width,
  right,
  height,
  label,
  isDisabled,
}: AccordionListProps<T>) => {
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLLIElement | null)[]>([]);

  const selectableIndexes = options
    .map((option, index) => (isDisabled?.(option) ? -1 : index))
    .filter((index) => index !== -1);

  const selectedIndex = options.findIndex(
    (option) => option === selectedOption,
  );
  const initialFocusIndex = selectableIndexes.includes(selectedIndex)
    ? selectedIndex
    : selectableIndexes[0];

  const [focusedIndex, setFocusedIndex] = useState(initialFocusIndex);

  useEffect(() => {
    if (focusedIndex !== undefined) itemRefs.current[focusedIndex]?.focus();
  }, [focusedIndex]);

  useEffect(() => {
    const hasFocusedSelectableOption =
      focusedIndex !== undefined && selectableIndexes.includes(focusedIndex);
    if (hasFocusedSelectableOption) return;

    setFocusedIndex(
      selectableIndexes.includes(selectedIndex)
        ? selectedIndex
        : selectableIndexes[0],
    );
  }, [focusedIndex, selectableIndexes, selectedIndex]);

  useEffect(() => {
    if (!onClickAway) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (listRef.current && !listRef.current.contains(e.target as Node)) {
        onClickAway();
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [onClickAway]);

  const moveFocus = (fromIndex: number, direction: 1 | -1) => {
    if (selectableIndexes.length === 0) return;
    const currentPosition = selectableIndexes.indexOf(fromIndex);
    const nextPosition =
      currentPosition === -1
        ? 0
        : (currentPosition + direction + selectableIndexes.length) %
          selectableIndexes.length;
    const nextIndex = selectableIndexes[nextPosition];
    setFocusedIndex(nextIndex);
    itemRefs.current[nextIndex]?.focus();
  };

  const handleItemKeyDown = (
    e: React.KeyboardEvent<HTMLLIElement>,
    index: number,
    option: T,
  ) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        moveFocus(index, 1);
        break;
      case "ArrowUp":
        e.preventDefault();
        moveFocus(index, -1);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (!isDisabled?.(option)) onSelect(option);
        break;
      case "Home":
        e.preventDefault();
        if (selectableIndexes.length > 0) {
          const firstIndex = selectableIndexes[0];
          setFocusedIndex(firstIndex);
          itemRefs.current[firstIndex]?.focus();
        }
        break;
      case "End":
        e.preventDefault();
        if (selectableIndexes.length > 0) {
          const lastIndex = selectableIndexes[selectableIndexes.length - 1];
          setFocusedIndex(lastIndex);
          itemRefs.current[lastIndex]?.focus();
        }
        break;
      case "Escape":
        e.preventDefault();
        onEscape?.();
        break;
    }
  };

  return (
    <div
      ref={listRef}
      className={dropdownStyles.accordionList}
      style={{
        width: width ?? "100%",
        right: right ?? "unset",
      }}
    >
      {label && (
        <p
          className={dropdownStyles.label}
          style={{
            width: "100%",
            borderBottom: "1px solid var(--col-border-primary)",
          }}
        >
          {label}
        </p>
      )}
      <ul
        id={id}
        role="listbox"
        aria-label={label}
        className={dropdownStyles.accordionListScroll}
        style={{ maxHeight: height ?? "200px" }}
      >
        {options.map((option, index) => {
          const isSelected = selectedOption === option;
          const disabled = isDisabled?.(option) ?? false;
          const labelText = labelKey
            ? (option[labelKey] as string)
            : (option as string);
          return (
            <li
              key={`option_${index}`}
              ref={(el) => (itemRefs.current[index] = el)}
              role="option"
              aria-selected={isSelected}
              aria-disabled={disabled || undefined}
              tabIndex={disabled ? -1 : focusedIndex === index ? 0 : -1}
              className={`
                ${dropdownStyles.accordionListItem}
                ${isSelected ? dropdownStyles.accordionListItem__selected : ""}
                ${disabled ? dropdownStyles.accordionListItem__disabled : ""}
              `}
              onClick={() => {
                if (!disabled) onSelect(option);
              }}
              onFocus={() => setFocusedIndex(index)}
              onKeyDown={(e) => handleItemKeyDown(e, index, option)}
            >
              <div className="flex gap-[var(--spacing-sm)] min-w-0 overflow-hidden flex-1">
                {leadingElement && leadingElement(option, isSelected)}
                <span className="truncate block" title={labelText}>
                  {labelText}
                </span>
              </div>
              {isSelected && (
                <CheckIcon width={16} height={16} className="shrink-0" />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default AccordionList;
